import { createAppStore } from '@app/core/stores';
import { CreateFragmentFormValues } from '../screens/add/useFragmentForm';

interface FragmentCreationState {
  exportEnabled: boolean;
  /** When true, after creating a fragment the app navigates to its detail page. */
  navigateAfterCreate: boolean;
  draftByStation: Record<string, Partial<CreateFragmentFormValues>>;

  // Actions
  toggleExport: (enabled: boolean) => void;
  setNavigateAfterCreate: (enabled: boolean) => void;
  setFormValues: (stationId: string, values: Partial<CreateFragmentFormValues>) => void;
  getFormValues: (stationId: string) => Partial<CreateFragmentFormValues> | undefined;
  clearFormValues: (stationId: string) => void;
}

/** Persisted subset (used for createAppStore second generic). */
type PersistedFragmentCreationState = Pick<
  FragmentCreationState,
  'exportEnabled' | 'navigateAfterCreate'
>;

export const useFragmentCreationStore = createAppStore<
  FragmentCreationState,
  PersistedFragmentCreationState
>('FragmentCreationStore', {
  persist: {
    name: 'app.fragments',
    version: 2,
    partialize: (state) => ({
      exportEnabled: state.exportEnabled,
      navigateAfterCreate: state.navigateAfterCreate,
    }),
  },
})((set, get) => ({
  // Initial state
  exportEnabled: false,
  navigateAfterCreate: false,
  draftByStation: {},

  // Toggle export enabled
  toggleExport: (enabled) => {
    set({ exportEnabled: enabled });
  },

  /** Remembers whether the next create should open the fragment page. */
  setNavigateAfterCreate: (enabled) => {
    set({ navigateAfterCreate: enabled });
  },

  // Set form values for a specific station
  setFormValues: (stationId, values) => {
    set((state) => ({
      draftByStation: {
        ...state.draftByStation,
        [stationId]: values,
      },
    }));
  },

  // Get form values for a specific station
  getFormValues: (stationId) => {
    return get().draftByStation[stationId];
  },

  // Clear form values for a specific station
  clearFormValues: (stationId) => {
    set((state) => {
      const { [stationId]: _, ...rest } = state.draftByStation;
      return { draftByStation: rest };
    });
  },
}));
